import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import * as TimelineService from "_services/timeline-service";
import { getDate } from "_utils/functions";

const initialState = {
    showTimelineSidebar: false,
    timelineRoute: "Employee Timeline",
    // auto approval of unplanned task
    autoAcceptUnplannedTask: false,
    movementTimer: 15,
    // switch for preBoard Feature
    preBoard: false,
    // timeline date 
    timelineDate: new Date(),
    // employee pagination flags 
    rowsPerPage: 25,
    currentPage: 1,
    pageCount: 5,
    currentRange: "",

    layout: 'default',

    defaultCheckInLocation: null,

    defaultArrivalCheckInLocation: null,

    defaultAdhocLocation: null,

    // switch for all flights Feature
    showAllFlights: false,

    // ramp bookings
    rampBookings: false,

    rampBookingsForAllLocations: false,
    rampBookingsForHCAndHS: false,

    // expand all flights
    expandAllFlights: false,

    // hide flights whose PRM tasks are all completed
    hideCompletedFlights: false,

    expandLocations: false,
    locationsTableView: false,

    thirtyMinLayout: true,
    removePrefix: false,

    // marshall task
    marshallTask: false,

    timelineAppSettings: null,
    timelineAppSettingsStatus: "idle",
    timelineAppSettingsError: null,
    updateTimelineAppSettingsStatus: "idle",
    updateTimelineAppSettingsError: null,

    // timeline display settings (in HOURS, not slots)
    timelineHoursToShow: 6,         // how many hours to display (default 6 hours on render — 3 either side of now)
    timelineHoursBack: 3,           // how many hours before "now" to start
    timelineStartLimit: false,
    timelineEndLimit: false,

    // periodic tick to force selector recomputation (updated every 60s)
    currentTimeTick: Date.now(),

    // device capabilities
    touchScreen: false,
    expandLiveMap: false,
    expandFlightEvents: false,

    // passenger modal
    passengerModal: false,
    selectedViewPassenger: null,

    // flight modal
    flightModal: false,

    // resizable layout dimensions
    timelineLeftWidthPercent: 94,
    timelineTopHeightVh: 45,


};


export const fetchTimelineAppSettings = createAsyncThunk(
    "timelineControl/fetchTimelineAppSettings",
    async (iata) => {
        return TimelineService.getTimelineAppSettings(iata);
    }
);

export const updateTimelineAppSettings = createAsyncThunk(
    "timelineControl/updateTimelineAppSettings",
    async (params) => {
        return TimelineService.updateTimelineAppSettings(params);
    }
);

const timelineControlSlice = createSlice({
    name: "timelineControl",
    initialState,
    reducers: {
        reset: (state) => initialState,
        setShowTimelineSidebar: (state, action) => {
            state.showTimelineSidebar = action.payload;
        },
        setTimelineRoute: (state, action) => {
            state.timelineRoute = action.payload;
        },
        setAutoAcceptUnplannedTask: (state, action) => {
            state.autoAcceptUnplannedTask = action.payload;
        },
        setMovementTimer: (state, action) => {
            state.movementTimer = action.payload;
        },
        setPreBoard: (state, action) => {
            state.preBoard = action.payload
        },
        setTimeLineDate: (state, action) => {
            state.timelineDate = action.payload
        },
        setRowsPerPage: (state, action) => {
            state.rowsPerPage = action.payload;
        },
        setCurrentPage: (state, action) => {
            state.currentPage = action.payload;
        },
        setPageCount: (state, action) => {
            state.pageCount = action.payload
        },
        setCurrentRange: (state, action) => {
            state.currentRange = action.payload
        },
        setExpandLocations: (state, action) => {
            state.expandLocations = action.payload
        },
        setLocationsTableView: (state, action) => {
            state.locationsTableView = action.payload
        },
        setDefaultCheckInLocation: (state, action) => {
            state.defaultCheckInLocation = action.payload;
        },
        setDefaultArrivalCheckInLocation: (state, action) => {
            state.defaultArrivalCheckInLocation = action.payload;
        },
        setDefaultAdhocLocation: (state, action) => {
            state.defaultAdhocLocation = action.payload;
        },
        setShowAllFlights: (state, action) => {
            state.showAllFlights = action.payload
        },
        setRampBookings: (state, action) => {
            state.rampBookings = action.payload
        },
        setRampBookingsForAllLocations: (state, action) => {
            state.rampBookingsForAllLocations = action.payload
        },
        setExpandAllFlights: (state, action) => {
            state.expandAllFlights = action.payload
        },
        setHideCompletedFlights: (state, action) => {
            state.hideCompletedFlights = action.payload
        },
        setThirtyMinLayout: (state, action) => {
            state.thirtyMinLayout = action.payload;
            // No need to adjust anything - we store hours, selector handles slots
        },
        setLayout: (state, action) => {
            state.layout = action.payload;
        },
        setRemovePaxNamePrefix: (state, action) => {
            state.removePrefix = action.payload;
        },
        setExpandLiveMap: (state, action) => {
            state.expandLiveMap = action.payload;
        },
        setExpandFlightEvents: (state, action) => {
            state.expandFlightEvents = action.payload;
        },
        setRampBookingsForHCAndHS: (state, action) => {
            state.rampBookingsForHCAndHS = action.payload
        },
        setPassengerModal: (state, action) => {
            state.passengerModal = action.payload;
        },
        setSelectedViewPassenger: (state, action) => {
            state.selectedViewPassenger = action.payload;
        },
        setFlightModal: (state, action) => {
            state.flightModal = action.payload;
        },
        setMarshallTask: (state, action) => {
            state.marshallTask = action.payload
        },
        setTouchScreen: (state, action) => {
            state.touchScreen = action.payload;
        },
        setTimelineLeftWidthPercent: (state, action) => {
            state.timelineLeftWidthPercent = action.payload;
        },
        setTimelineTopHeightVh: (state, action) => {
            state.timelineTopHeightVh = action.payload;
        },
        setTimeSlots: (state, action) => {
            state.timelineHoursToShow = action.payload;
        },
        // Add 1 hour to the end of timeline
        increaseTimelineSlots: (state, action) => {
            const payload = action.payload;
            const hoursToAdd = typeof payload === "number" ? payload : (payload?.hoursToAdd ?? 1);
            const iata = payload?.authority;

            let maxHours = 24;

            // check if dublin - limit to 8 hours max
            if (iata === 'DUB') {
                maxHours = 12;
            }

            // check if cork - limit to 20 hours max
            if (iata === 'ORK') {
                maxHours = 20;
            }

            const newTotal = state.timelineHoursToShow + hoursToAdd;
            if (newTotal >= maxHours) {
                state.timelineHoursToShow = maxHours;
                state.timelineEndLimit = true;
            } else {
                state.timelineHoursToShow = newTotal;
                state.timelineEndLimit = false;
            }
        },
        // Go back 1 more hour at the start
        increaseTimelineStart: (state, action) => {
            const hoursToAdd = action.payload || 1;
            const maxHoursBack = 6; // max 3 hours back from now

            const newTotal = state.timelineHoursBack + hoursToAdd;
            if (newTotal >= maxHoursBack) {
                state.timelineHoursBack = maxHoursBack;
                state.timelineStartLimit = true;
            } else {
                state.timelineHoursBack = newTotal;
                state.timelineStartLimit = false;
            }
        },
        resetTimelineSlots: (state, action) => {
            const maxHours = 12;
            const defaultHoursToShow = 6; // 3 hours either side of "now"
            const defaultHoursBack = 3;

            state.timelineHoursToShow = defaultHoursToShow;
            state.timelineHoursBack = defaultHoursBack;
            state.timelineStartLimit = false;
            state.timelineEndLimit = defaultHoursToShow >= maxHours;
        },
        updateTimeTick: (state) => {
            state.currentTimeTick = Date.now();
        }
    },
    extraReducers: {
        [fetchTimelineAppSettings.pending]: (state) => {
            state.timelineAppSettingsStatus = "loading";
            state.timelineAppSettingsError = null;
        },
        [fetchTimelineAppSettings.fulfilled]: (state, action) => {
            state.timelineAppSettingsStatus = "succeeded";
            if (action.payload?.settings !== undefined) {
                state.timelineAppSettings = action.payload.settings;
            }
        },
        [fetchTimelineAppSettings.rejected]: (state, action) => {
            state.timelineAppSettingsStatus = "failed";
            state.timelineAppSettingsError = action.error?.message || "Failed to fetch timeline app settings";
        },

        [updateTimelineAppSettings.pending]: (state) => {
            state.updateTimelineAppSettingsStatus = "loading";
            state.updateTimelineAppSettingsError = null;
        },
        [updateTimelineAppSettings.fulfilled]: (state, action) => {
            state.updateTimelineAppSettingsStatus = "succeeded";
        },
        [updateTimelineAppSettings.rejected]: (state, action) => {
            state.updateTimelineAppSettingsStatus = "failed";
            state.updateTimelineAppSettingsError = action.error?.message || "Failed to update timeline app settings";
        },
    },
});

export const {
    reset,
    setShowTimelineSidebar,
    setTimelineRoute,
    setAutoAcceptUnplannedTask,
    setMovementTimer,
    setPreBoard,
    setTimeLineDate,
    setRowsPerPage,
    setDefaultCheckInLocation,
    setDefaultArrivalCheckInLocation,
    setDefaultAdhocLocation,
    setShowAllFlights,
    setCurrentPage,
    setPageCount,
    setCurrentRange,
    setRampBookings,
    setRampBookingsForAllLocations,
    setExpandAllFlights,
    setHideCompletedFlights,
    setExpandLocations,
    setLocationsTableView,
    setThirtyMinLayout,
    setLayout,
    setRemovePaxNamePrefix,
    setRampBookingsForHCAndHS,
    setMarshallTask,
    setTouchScreen,
    increaseTimelineSlots,
    increaseTimelineStart,
    setTimeSlots,
    resetTimelineSlots,
    setExpandLiveMap,
    setExpandFlightEvents,
    setPassengerModal,
    setSelectedViewPassenger,
    setFlightModal,
    setTimelineLeftWidthPercent,
    setTimelineTopHeightVh,
    updateTimeTick
} = timelineControlSlice.actions;

export const selectLayout = (state) => state.timelineControl.layout;
export const selectExpandLocations = (state) => state.timelineControl.expandLocations;
export const selectLocationsTableView = (state) => state.timelineControl.locationsTableView;
export const selectShowTimelineSidebar = (state) => state.timelineControl.showTimelineSidebar;
export const selectTimelineRoute = (state) => state.timelineControl.timelineRoute;
export const selectAutoAcceptUnplannedTask = (state) => state.timelineControl.autoAcceptUnplannedTask;
export const selectMovementTimer = (state) => state.timelineControl.movementTimer;
export const selectPreBoard = (state) => state.timelineControl.preBoard;
export const selectTimelineDate = (state) => state.timelineControl.timelineDate;

//rows per page selector
export const selectRowsPerPage = (state) => state.timelineControl.rowsPerPage;
export const selectCurrentPage = (state) => state.timelineControl.currentPage;
export const selectPageCount = (state) => state.timelineControl.pageCount;
export const selectCurrentRange = (state) => state.timelineControl.currentRange;

export const selectDefaultCheckInLocation = (state) => state.timelineControl.defaultCheckInLocation;
export const selectDefaultArrivalCheckInLocation = (state) => state.timelineControl.defaultArrivalCheckInLocation;
export const selectDefaultAdhocLocation = (state) => state.timelineControl.defaultAdhocLocation;
// export const selectShowAllFlights = (state) => state.timelineControl.showAllFlights;


// Base selector for timeline control
const selectTimelineControlState = (state) => state.timelineControl;

export const selectShowAllFlights = createSelector(
    [selectTimelineControlState],
    (timelineControl) => timelineControl.showAllFlights
);

// OPTIMIZED: Memoized selectors
export const selectRampBookings = createSelector(
    [selectTimelineControlState],
    (timelineControl) => timelineControl.rampBookings
);

export const selectRampBookingsForAllLocations = createSelector(
    [selectTimelineControlState],
    (timelineControl) => timelineControl.rampBookingsForAllLocations
);

export const selectRampBookingsForHCAndHS = createSelector(
    [selectTimelineControlState],
    (timelineControl) => timelineControl.rampBookingsForHCAndHS
);

export const selectExpandAllFlights = createSelector(
    [selectTimelineControlState],
    (timelineControl) => timelineControl.expandAllFlights
);

export const selectHideCompletedFlights = createSelector(
    [selectTimelineControlState],
    (timelineControl) => timelineControl.hideCompletedFlights
);

export const selectThirtyMinLayout = createSelector(
    [selectTimelineControlState],
    (timelineControl) => timelineControl.thirtyMinLayout
);

export const selectMarshallTask = createSelector(
    [selectTimelineControlState],
    (timelineControl) => timelineControl.marshallTask
);

// Helper function to determine which slot a time belongs to
export const getSlot = (timeString, timelineSlots, thirtyMinLayout) => {
    if (!timeString || !Array.isArray(timelineSlots) || timelineSlots.length === 0) {
        return null;
    }

    // Parse the time string (format: "HH:MM")
    const [hours, minutes] = timeString.split(':').map(Number);
    const timeInMinutes = hours * 60 + minutes;

    if (thirtyMinLayout) {
        // 30-minute slots: find exact match or closest slot
        for (let i = 0; i < timelineSlots.length; i++) {
            const [slotHours, slotMinutes] = timelineSlots[i].split(':').map(Number);
            const slotInMinutes = slotHours * 60 + slotMinutes;

            // Check if time falls within this 30-minute slot
            if (timeInMinutes >= slotInMinutes && timeInMinutes < slotInMinutes + 30) {
                return timelineSlots[i];
            }
        }
    } else {
        // Hour slots: find which hour slot the time belongs to
        for (let i = 0; i < timelineSlots.length; i++) {
            const [slotHours] = timelineSlots[i].split(':').map(Number);
            const slotInMinutes = slotHours * 60;

            // Check if time falls within this hour slot
            if (timeInMinutes >= slotInMinutes && timeInMinutes < slotInMinutes + 60) {
                return timelineSlots[i];
            }
        }
    }

    // Return last slot if time is beyond all slots
    return timelineSlots[timelineSlots.length - 1];
};

// Memoized selector - computes time slots based on hours settings
export const selectTimelineIndex = createSelector(
    [
        (state) => state.timelineControl.timelineHoursToShow,
        (state) => state.timelineControl.timelineHoursBack,
        (state) => state.timelineControl.thirtyMinLayout,
        (state) => state.timelineControl.timelineDate
    ],
    (hoursToShow, hoursBack, thirtyMinLayout, timelineDate) => {
        const now = new Date();
        const currentDay = getDate(now);
        const timelineDay = getDate(timelineDate);

        // If viewing a different day, show the full day (for timeline-v1)
        if (currentDay !== timelineDay) {
            const slots = [];
            if (thirtyMinLayout) {
                for (let hour = 0; hour < 24; hour++) {
                    const h = hour.toString().padStart(2, "0");
                    slots.push(`${h}:00`);
                    slots.push(`${h}:30`);
                }
            } else {
                for (let hour = 0; hour < 24; hour++) {
                    const h = hour.toString().padStart(2, "0");
                    slots.push(`${h}:00`);
                }
            }
            return slots;
        }

        // For TODAY: start X hours back from now, show Y hours total
        const start = new Date(now);
        start.setHours(start.getHours() - hoursBack);
        start.setMinutes(0, 0, 0); // round to hour

        /* Don't go before midnight
        if (start.getDate() !== now.getDate()) {
            start.setHours(0, 0, 0, 0);
            start.setDate(now.getDate());
        }
        */

        const slots = [];
        const cursor = new Date(start);

        // Calculate number of slots based on layout
        const slotCount = thirtyMinLayout ? hoursToShow * 2 : hoursToShow;

        if (thirtyMinLayout) {
            for (let i = 0; i < slotCount; i++) {
                const h = cursor.getHours().toString().padStart(2, "0");
                const m = cursor.getMinutes().toString().padStart(2, "0");
                slots.push(`${h}:${m}`);
                cursor.setMinutes(cursor.getMinutes() + 30);
            }
        } else {
            for (let i = 0; i < slotCount; i++) {
                const h = cursor.getHours().toString().padStart(2, "0");
                slots.push(`${h}:00`);
                cursor.setHours(cursor.getHours() + 1);
            }
        }

        return slots;
    }
);

// flags to disable further timeline adjustments
export const selectStartLimit = (state) => state.timelineControl.timelineStartLimit;
export const selectEndLimit = (state) => state.timelineControl.timelineEndLimit;

export const selectRemovePaxNamePrefix = (state) => state.timelineControl.removePrefix;
export const selectTimelineAppSettings = (state) => state.timelineControl.timelineAppSettings;
export const selectTimelineAppSettingsStatus = (state) => state.timelineControl.timelineAppSettingsStatus;
export const selectTimelineAppSettingsError = (state) => state.timelineControl.timelineAppSettingsError;
export const selectTouchScreen = (state) => state.timelineControl.touchScreen;

export const selectExpandLiveMap = (state) => state.timelineControl.expandLiveMap;
export const selectExpandFlightEvents = (state) => state.timelineControl.expandFlightEvents;
export const selectPassengerModal = (state) => state.timelineControl.passengerModal;
export const selectSelectedViewPassenger = (state) => state.timelineControl.selectedViewPassenger;
export const selectFlightModal = (state) => state.timelineControl.flightModal;

export const selectTimelineLeftWidthPercent = (state) => state.timelineControl.timelineLeftWidthPercent;
export const selectTimelineTopHeightVh = (state) => state.timelineControl.timelineTopHeightVh;

export default timelineControlSlice.reducer;